import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  BB_LOTTERY_ISSUE_NAME_I18N_KEYS,
  BbSeason,
  BbLotteryIssue,
  BbLotteryTicketLadder,
  BbLotteryTicketTier,
  BbLotteryTicketTierGroup,
  bbCreateLotteryIssue,
  bbLotteryIssueListLabel,
  bbExecuteLotteryDraw,
  bbGetFeatureFlags,
  bbGetSeasons,
  bbGetLotteryIssues,
  bbGetLotteryTicketLadder,
  bbSetFeatureFlag,
  bbSetLotteryTicketLadder,
  buildLotteryIssueNameI18nPayload,
  mergeLotteryIssueNameI18nFromApi,
  bbUpdateSeason,
  bbUpdateLotteryIssue,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import { PermissionData } from '../../../entities/permission.entity';
import { hasPermission } from '../../../utils/has-permission';

// POST /blind-box/lottery-ticket-ladder requires
// bb-holiday-lottery:update OR bb-lottery-tickets:update
const TICKET_LADDER_UPDATE_PERMS = [
  { resource: 'bb-holiday-lottery', action: 'update' },
  { resource: 'bb-lottery-tickets', action: 'update' },
];
// PUT /blind-box/seasons requires bb-activity-basic:update OR bb-holiday-lottery:update
const SEASON_UPDATE_PERMS = [
  { resource: 'bb-activity-basic', action: 'update' },
  { resource: 'bb-holiday-lottery', action: 'update' },
];

/** Ticket 阶梯的四组配置，写入语义一致：不传=不变 / 非空=全量替换 / []=清空 */
const TICKET_TIER_GROUPS: {
  key: BbLotteryTicketTierGroup;
  titleKey: string;
  tierKeys: [string, string];
}[] = [
  {
    key: 'volumeTiers',
    titleKey: 'module.blindBox.ticketDailyVol',
    tierKeys: ['module.blindBox.ticketDailyVol1', 'module.blindBox.ticketDailyVol2'],
  },
  {
    key: 'predictVolumeTiers',
    titleKey: 'module.blindBox.ticketDailyPredictVol',
    tierKeys: [
      'module.blindBox.ticketDailyPredictVol1',
      'module.blindBox.ticketDailyPredictVol2',
    ],
  },
  {
    key: 'leverageVolumeTiers',
    titleKey: 'module.blindBox.ticketDailyLeverageVol',
    tierKeys: [
      'module.blindBox.ticketDailyLeverageVol1',
      'module.blindBox.ticketDailyLeverageVol2',
    ],
  },
  {
    key: 'tapGridVolumeTiers',
    titleKey: 'module.blindBox.ticketDailyTapGridVol',
    tierKeys: ['module.blindBox.ticketDailyTapGridVol1', 'module.blindBox.ticketDailyTapGridVol2'],
  },
];

/** 期次名称多语言：次要配置，默认折叠 */
function LotteryIssueNameI18nAccordion({ children }: { children: React.ReactNode }) {
  const t = useTranslate();
  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Typography variant="subtitle2" color="text.secondary">
          {t('module.blindBox.lotteryIssueNameI18nTitle')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('module.blindBox.lotteryIssueNameI18nHint')}
        </Typography>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export function SectionLottery({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionData>();
  const permissionList = permissions?.permissions;
  // POST /blind-box/feature-flags requires
  // bb-leaderboard:update OR bb-activity-basic:update OR bb-holiday-lottery:update
  const canToggleFlag =
    hasPermission(permissionList, 'bb-leaderboard', 'update') ||
    hasPermission(permissionList, 'bb-activity-basic', 'update') ||
    hasPermission(permissionList, 'bb-holiday-lottery', 'update');
  const [loading, setLoading] = useState(false);
  const [seasons, setSeasons] = useState<BbSeason[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [issues, setIssues] = useState<BbLotteryIssue[]>([]);
  const [selIssueId, setSelIssueId] = useState<number | null>(null);
  const [lotteryEnabled, setLotteryEnabled] = useState<boolean>(false);
  const [ladder, setLadder] = useState<BbLotteryTicketLadder | null>(null);
  const [seasonEdit, setSeasonEdit] = useState<BbSeason | null>(null);
  const [issueDlg, setIssueDlg] = useState(false);
  const [creatingIssue, setCreatingIssue] = useState(false);
  const [issueEdit, setIssueEdit] = useState<BbLotteryIssue | null>(null);
  const [iform, setIform] = useState({
    issueNumber: '',
    name: '',
    nameI18n: mergeLotteryIssueNameI18nFromApi(undefined),
    startTime: '',
    endTime: '',
    ticketDigitCount: 6,
    prizeTiers: [] as {
      tierName: string;
      winnersCount: number;
      rewardAmount: string;
      displayOrder: number;
    }[],
  });

  const toIsoOrEmpty = (value: Dayjs | null) => (value ? value.toISOString() : '');

  const handleSeasonEditStartChange = (value: Dayjs | null) => {
    if (!seasonEdit) return;
    setSeasonEdit((p) => (p ? { ...p, startTime: toIsoOrEmpty(value) } : p));
  };
  const handleSeasonEditEndChange = (value: Dayjs | null) => {
    if (!seasonEdit) return;
    setSeasonEdit((p) => (p ? { ...p, endTime: toIsoOrEmpty(value) } : p));
  };

  const handleIssueCreateStartChange = (value: Dayjs | null) => {
    setIform((p) => ({ ...p, startTime: toIsoOrEmpty(value) }));
  };
  const handleIssueCreateEndChange = (value: Dayjs | null) => {
    setIform((p) => ({ ...p, endTime: toIsoOrEmpty(value) }));
  };
  const handleIssueEditStartChange = (value: Dayjs | null) => {
    if (!issueEdit) return;
    setIssueEdit((p) => (p ? { ...p, startTime: toIsoOrEmpty(value) } : p));
  };
  const handleIssueEditEndChange = (value: Dayjs | null) => {
    if (!issueEdit) return;
    setIssueEdit((p) => (p ? { ...p, endTime: toIsoOrEmpty(value) } : p));
  };

  const lotteryIssueStatusLabel = (status: string | undefined) => {
    const raw = String(status || '').trim();
    if (!raw) return '—';
    const key = `module.blindBox.lotteryIssueStatus_${raw.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : raw;
  };
  const loadSeasons = useCallback(() => {
    bbGetSeasons(50, 0).then((r) => setSeasons(r.seasons || []));
  }, []);

  const loadLotteryEnabled = useCallback(() => {
    bbGetFeatureFlags()
      .then((rows) => {
        const hit = rows.find((r) => r.key === 'lottery_enabled');
        setLotteryEnabled(!!hit?.value);
      })
      .catch(() => setLotteryEnabled(false));
  }, []);

  const loadLadder = useCallback(() => {
    bbGetLotteryTicketLadder()
      .then(setLadder)
      .catch(() => setLadder(null));
  }, []);

  useEffect(() => {
    loadSeasons();
    loadLotteryEnabled();
    loadLadder();
  }, [loadSeasons, loadLotteryEnabled, loadLadder, refreshKey]);

  useEffect(() => {
    if (sel) return;
    if (!seasons.length) return;
    // 默认选一个“进行中”或最新的周期，避免页面初始为空
    const active =
      seasons.find((c) => c.status === 'active') || seasons.find((c) => c.status === 'in_progress');
    setSel(active?.id || seasons[0].id);
  }, [seasons, sel]);

  useEffect(() => {
    if (sel)
      bbGetLotteryIssues(sel)
        .then(setIssues)
        .catch(() => setIssues([]));
    else setIssues([]);
  }, [sel]);

  useEffect(() => {
    if (!issues.length) {
      setSelIssueId(null);
      return;
    }
    setSelIssueId((prev) => {
      if (prev && issues.some((i) => i.id === prev)) return prev;
      // 默认选最新一期（按 endTime desc）
      const latest = [...issues].sort((a, b) => {
        const at = typeof a.endTime === 'number' ? a.endTime : Number(a.endTime ?? 0);
        const bt = typeof b.endTime === 'number' ? b.endTime : Number(b.endTime ?? 0);
        return bt - at; // desc
      })[0];
      return latest?.id ?? null;
    });
  }, [issues]);

  const selectedIssue = useMemo(
    () => (selIssueId ? issues.find((i) => i.id === selIssueId) || null : null),
    [issues, selIssueId]
  );

  const truncateHash = (hash?: string | null) => {
    const h = String(hash || '');
    if (!h) return '—';
    if (h.length <= 14) return h;
    return `${h.slice(0, 6)}…${h.slice(-4)}`;
  };

  useEffect(() => {
    if (selectedIssue)
      setIssueEdit({
        ...selectedIssue,
        nameI18n: mergeLotteryIssueNameI18nFromApi(selectedIssue.nameI18n),
      });
    else setIssueEdit(null);
  }, [selectedIssue]);

  const toggleLotteryEnabled = async (value: boolean) => {
    setLoading(true);
    try {
      await bbSetFeatureFlag('lottery_enabled', value);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setLotteryEnabled(value);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const saveLadder = async () => {
    if (!ladder) return;
    setLoading(true);
    try {
      await bbSetLotteryTicketLadder(ladder);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      loadLadder();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const isFiniteNumberString = (v: unknown) => {
    const s = String(v ?? '').trim();
    if (s === '') return false;
    const n = Number(s);
    return Number.isFinite(n);
  };

  const canAddTierRow = (tier?: { threshold?: string; ticketCount?: number }) => {
    if (!tier) return true;
    return isFiniteNumberString(tier.threshold) && Number.isFinite(Number(tier.ticketCount));
  };

  const tiersOf = (key: BbLotteryTicketTierGroup): BbLotteryTicketTier[] => ladder?.[key] ?? [];

  const setTiers = (key: BbLotteryTicketTierGroup, tiers: BbLotteryTicketTier[]) => {
    if (!ladder) return;
    setLadder({ ...ladder, [key]: tiers });
  };

  const addTierRow = (key: BbLotteryTicketTierGroup) => {
    if (!ladder) return;
    const list = tiersOf(key);
    const last = list[list.length - 1];
    if (!canAddTierRow(last)) {
      notify(t('module.blindBox.invalidTierRow'), { type: 'warning' });
      return;
    }
    setTiers(key, [...list, { threshold: '', ticketCount: 0 }]);
  };

  const removeTierRow = (key: BbLotteryTicketTierGroup, idx: number) => {
    if (!ladder) return;
    setTiers(
      key,
      tiersOf(key).filter((_, i) => i !== idx)
    );
  };

  const saveSeasonEdit = async () => {
    if (!seasonEdit) return;
    setLoading(true);
    try {
      await bbUpdateSeason({
        id: seasonEdit.id,
        name: seasonEdit.name,
        startTime: seasonEdit.startTime,
        endTime: seasonEdit.endTime,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setSeasonEdit(null);
      loadSeasons();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const createIssue = async () => {
    if (!sel) return;
    const tiers =
      iform.prizeTiers.length > 0
        ? iform.prizeTiers
        : [{ tierName: 'A', winnersCount: 1, rewardAmount: '1', displayOrder: 1 }];

    const bad = tiers.findIndex((x) => Number(x.rewardAmount) <= 0 || !Number.isFinite(Number(x.rewardAmount)));
    if (bad >= 0) {
      notify(`prizeTiers[${bad}].rewardAmount must be > 0`, { type: 'warning' });
      return;
    }
    const digits = Number(iform.ticketDigitCount);
    if (!Number.isInteger(digits) || digits < 1 || digits > 10) {
      notify(t('module.blindBox.ticketDigitCountInvalid'), { type: 'warning' });
      return;
    }
    setCreatingIssue(true);
    try {
      const nameI18nPayload = buildLotteryIssueNameI18nPayload(iform.nameI18n);
      await bbCreateLotteryIssue({
        seasonId: sel,
        issueNumber: iform.issueNumber,
        name: iform.name,
        ...(nameI18nPayload ? { nameI18n: nameI18nPayload } : {}),
        ...(iform.startTime.trim() ? { startTime: iform.startTime.trim() } : {}),
        endTime: iform.endTime,
        ticketDigitCount: digits,
        prizeTiers: tiers,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setIssueDlg(false);
      setIform({
        issueNumber: '',
        name: '',
        nameI18n: mergeLotteryIssueNameI18nFromApi(undefined),
        startTime: '',
        endTime: '',
        ticketDigitCount: 4,
        prizeTiers: [],
      });
      bbGetLotteryIssues(sel).then(setIssues);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setCreatingIssue(false);
    }
  };

  const saveIssueEdit = useCallback(async () => {
    if (!issueEdit) return;
    const normTime = (v: unknown) => String(v ?? '').trim();
    const startNorm = normTime(issueEdit.startTime);
    const origStartNorm = normTime(selectedIssue?.startTime);
    const startTimeChanged = startNorm !== origStartNorm;
    setLoading(true);
    try {
      const nameI18nPayload = buildLotteryIssueNameI18nPayload(
        mergeLotteryIssueNameI18nFromApi(issueEdit.nameI18n),
      );
      await bbUpdateLotteryIssue(issueEdit.id, {
        name: issueEdit.name,
        ...(nameI18nPayload ? { nameI18n: nameI18nPayload } : {}),
        ...(startTimeChanged && startNorm ? { startTime: startNorm } : {}),
        endTime: issueEdit.endTime,
        prizeTiers: issueEdit.prizeTiers.map((tier) => ({
          tierName: tier.tierName,
          winnersCount: tier.winnersCount,
          rewardAmount: tier.rewardAmount,
          // winningNumbers: tier.winningNumbers,
          displayOrder: tier.displayOrder,
        })),
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      // 保留编辑态，但刷新列表数据
      if (sel) bbGetLotteryIssues(sel).then(setIssues);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [issueEdit, notify, sel, selectedIssue, t]);

  const drawIssue = async (issueId: number) => {
    setLoading(true);
    try {
      await bbExecuteLotteryDraw(issueId);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      if (sel) bbGetLotteryIssues(sel).then(setIssues);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      {/* 1 + 2：标题/说明 | 期次操作 | 活动状态 */}
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1">{t('module.blindBox.lotterySettingsTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('module.blindBox.lotterySettingsHint')}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            flexShrink={0}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
          >
            <PermissionButton resource="bb-holiday-lottery" action="create">
              <Button
                size="small"
                variant="outlined"
                disabled={loading || !sel}
                startIcon={<AddIcon />}
                onClick={() => {
                  setIform({
                    issueNumber: '',
                    name: '',
                    nameI18n: mergeLotteryIssueNameI18nFromApi(undefined),
                    startTime: '',
                    endTime: '',
                    ticketDigitCount: 4,
                    prizeTiers: [{ tierName: 'A', winnersCount: 1, rewardAmount: '1', displayOrder: 1 }],
                  });
                  setIssueDlg(true);
                }}
              >
                {t('module.blindBox.createIssue')}
              </Button>
            </PermissionButton>
            <PermissionButton resource="bb-holiday-lottery" action="update">
              <Button size="small" variant="contained" disabled={loading || !issueEdit} onClick={saveIssueEdit}>
                {t('actions.save')}
              </Button>
            </PermissionButton>
          </Stack>

          <Box sx={{ flexShrink: 0 }}>
            <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('module.blindBox.lotteryActivityStatus')}
              </Typography>
              <Switch
                checked={lotteryEnabled}
                disabled={loading || !canToggleFlag}
                onChange={(e) => toggleLotteryEnabled(e.target.checked)}
              />
            </Stack>
          </Box>
        </Stack>

        <Stack spacing={2}>
          <TextField
            select
            label={t('module.blindBox.selectSeason')}
            value={sel || ''}
            onChange={(e) => setSel(e.target.value || null)}
            fullWidth
          >
            <MenuItem value="">{t('module.blindBox.none')}</MenuItem>
            {seasons.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name} ({c.seasonNumber})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t('module.blindBox.currentIssue')}
            value={selIssueId || ''}
            onChange={(e) => setSelIssueId(e.target.value ? Number(e.target.value) : null)}
            fullWidth
            disabled={!issues.length}
          >
            <MenuItem value="">{t('module.blindBox.none')}</MenuItem>
            {issues.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {bbLotteryIssueListLabel(i)}
                {i.status ? ` · ${lotteryIssueStatusLabel(i.status)}` : ''}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={t('module.blindBox.lotteryIssueStartTime')}
                value={issueEdit?.startTime ? dayjs(issueEdit.startTime) : null}
                onChange={handleIssueEditStartChange}
                disabled={loading || !issueEdit}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={t('module.blindBox.lotteryDrawTime')}
                value={issueEdit?.endTime ? dayjs(issueEdit.endTime) : null}
                onChange={handleIssueEditEndChange}
                disabled={loading || !issueEdit}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </LocalizationProvider>
          </Stack>

          <TextField
            label={t('module.blindBox.lotteryPeriodSubtitle')}
            value={issueEdit?.name || ''}
            onChange={(e) => issueEdit && setIssueEdit({ ...issueEdit, name: e.target.value })}
            disabled={loading || !issueEdit}
            fullWidth
          />
          <LotteryIssueNameI18nAccordion>
            <Grid container spacing={1.5}>
              {BB_LOTTERY_ISSUE_NAME_I18N_KEYS.map((loc) => (
                <Grid item xs={12} sm={6} key={loc}>
                  <TextField
                    size="small"
                    label={t(`module.blindBox.lotteryIssueNameI18n_${loc}`)}
                    value={issueEdit ? mergeLotteryIssueNameI18nFromApi(issueEdit.nameI18n)[loc] : ''}
                    onChange={(e) => {
                      if (!issueEdit) return;
                      const prev = mergeLotteryIssueNameI18nFromApi(issueEdit.nameI18n);
                      setIssueEdit({
                        ...issueEdit,
                        nameI18n: { ...prev, [loc]: e.target.value },
                      });
                    }}
                    disabled={loading || !issueEdit}
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>
          </LotteryIssueNameI18nAccordion>
          <TextField
            label={t('module.blindBox.lotteryIssueStatus')}
            value={lotteryIssueStatusLabel(issueEdit?.status)}
            disabled
            fullWidth
          />
          <TextField
            label={t('module.blindBox.ticketDigitCount')}
            value={
              issueEdit?.ticketDigitCount !== undefined && issueEdit.ticketDigitCount !== null
                ? String(issueEdit.ticketDigitCount)
                : ''
            }
            disabled
            fullWidth
            helperText={t('module.blindBox.ticketDigitCountReadOnlyHint')}
          />
        </Stack>
      </Box>

      {/* 3 + 4 + 5：Prize pool structure */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle1">{t('module.blindBox.prizePoolStructure')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('module.blindBox.prizePoolStructureHint')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={!issueEdit || loading}
            onClick={() => {
              if (!issueEdit) return;
              const nextOrder =
                (issueEdit.prizeTiers?.reduce((m, x) => Math.max(m, x.displayOrder || 0), 0) || 0) +
                1;
              setIssueEdit({
                ...issueEdit,
                prizeTiers: [
                  ...(issueEdit.prizeTiers || []),
                  {
                    tierName: '',
                    winnersCount: 1,
                    rewardAmount: '1',
                    displayOrder: nextOrder,
                    winningNumbers: [],
                  },
                ],
              });
            }}
          >
            {t('module.blindBox.addPrizeTier')}
          </Button>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('module.blindBox.colTier')}</TableCell>
                <TableCell>{t('module.blindBox.colTierName')}</TableCell>
                <TableCell>{t('module.blindBox.colRewardAmount')}</TableCell>
                <TableCell>{t('module.blindBox.colWinnersCount')}</TableCell>
                <TableCell align="right">{t('module.blindBox.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(issueEdit?.prizeTiers || []).map((tier, idx) => (
                <TableRow key={tier.displayOrder ?? idx}>
                  <TableCell sx={{ width: 80 }}>{tier.displayOrder}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={tier.tierName}
                      onChange={(e) => {
                        if (!issueEdit) return;
                        const next = [...issueEdit.prizeTiers];
                        next[idx] = { ...tier, tierName: e.target.value };
                        setIssueEdit({ ...issueEdit, prizeTiers: next });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={tier.rewardAmount}
                      onChange={(e) => {
                        if (!issueEdit) return;
                        const next = [...issueEdit.prizeTiers];
                        next[idx] = { ...tier, rewardAmount: e.target.value };
                        setIssueEdit({ ...issueEdit, prizeTiers: next });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={tier.winnersCount}
                      onChange={(e) => {
                        if (!issueEdit) return;
                        const next = [...issueEdit.prizeTiers];
                        next[idx] = { ...tier, winnersCount: parseInt(e.target.value, 10) || 0 };
                        setIssueEdit({ ...issueEdit, prizeTiers: next });
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      disabled={!issueEdit || loading}
                      onClick={() => {
                        if (!issueEdit) return;
                        const next = issueEdit.prizeTiers.filter((_, i) => i !== idx);
                        setIssueEdit({ ...issueEdit, prizeTiers: next });
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!issueEdit?.prizeTiers?.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      {t('module.blindBox.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 6：Ticket acquisition ladder */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">{t('module.blindBox.ticketLadderTitle')}</Typography>
          <PermissionGuard anyOf={TICKET_LADDER_UPDATE_PERMS} fallback={null} loadingFallback={null}>
            <Button variant="contained" disabled={loading || !ladder} onClick={saveLadder}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
          {t('module.blindBox.ticketLadderHint')}
        </Typography>

        <Stack spacing={2}>
          {/*<TextField*/}
          {/*  label={t('module.blindBox.ticketPerBoxOpen')}*/}
          {/*  value={ladder?.boxOpenTickets ?? ''}*/}
          {/*  onChange={(e) =>*/}
          {/*    ladder &&*/}
          {/*    setLadder({*/}
          {/*      ...ladder,*/}
          {/*      boxOpenTickets: parseInt(e.target.value, 10) || 0,*/}
          {/*    })*/}
          {/*  }*/}
          {/*  disabled={loading || !ladder}*/}
          {/*  placeholder="0"*/}
          {/*/>*/}

          {TICKET_TIER_GROUPS.map((group) => {
            const tiers = tiersOf(group.key);
            // 超过两档时复用第一档文案，仅作为输入框标题
            const tierLabel = (idx: number) => t(group.tierKeys[idx === 1 ? 1 : 0]);
            return (
              <Box key={group.key}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(group.titleKey)}
                </Typography>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={loading || !ladder}
                    onClick={() => addTierRow(group.key)}
                  >
                    {t('module.blindBox.addTier')}
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  {tiers.map((tier, idx) => (
                    <Stack
                      key={`${group.key}-${idx}`}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      alignItems={{ md: 'center' }}
                    >
                      <TextField
                        label={`${tierLabel(idx)} ${t('module.blindBox.ticketDailyVolThresholdPrefix')} (USDC)`}
                        value={tier.threshold ?? ''}
                        onChange={(e) => {
                          const next = [...tiers];
                          next[idx] = { ...tier, threshold: e.target.value };
                          setTiers(group.key, next);
                        }}
                        disabled={loading || !ladder}
                        placeholder="0"
                        fullWidth
                      />
                      <TextField
                        label={tierLabel(idx)}
                        value={tier.ticketCount ?? ''}
                        onChange={(e) => {
                          const next = [...tiers];
                          next[idx] = { ...tier, ticketCount: parseInt(e.target.value, 10) || 0 };
                          setTiers(group.key, next);
                        }}
                        disabled={loading || !ladder}
                        placeholder="0"
                        sx={{ width: { xs: '100%', md: 240 } }}
                      />
                      <IconButton
                        size="small"
                        disabled={loading || !ladder}
                        onClick={() => removeTierRow(group.key, idx)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  {!tiers.length && (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* 7：Lottery results configuration */}
      <Box>
        <Typography variant="subtitle1">{t('module.blindBox.lotteryResultsTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
          {t('module.blindBox.lotteryResultsHint')}
        </Typography>
        <Stack spacing={2}>
          <TextField
            select
            label={t('module.blindBox.lotteryResultsSelectSession')}
            value={selIssueId || ''}
            onChange={(e) => setSelIssueId(e.target.value ? Number(e.target.value) : null)}
            fullWidth
            disabled={!issues.length}
          >
            <MenuItem value="">{t('module.blindBox.none')}</MenuItem>
            {issues.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {bbLotteryIssueListLabel(i)}
                {i.status ? ` · ${lotteryIssueStatusLabel(i.status)}` : ''}
                {i.endTime ? ` (${dayjs(i.endTime).format('YYYY-MM-DD HH:mm')})` : ''}
              </MenuItem>
            ))}
          </TextField>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('module.blindBox.lotteryResultsColPrizeTier')}</TableCell>
                  <TableCell sx={{ width: 90 }}>
                    {t('module.blindBox.lotteryResultsColRank')}
                  </TableCell>
                  <TableCell>{t('module.blindBox.lotteryResultsColWinningNumber')}</TableCell>
                  <TableCell>{t('module.blindBox.lotteryResultsColTxHash')}</TableCell>
                  <TableCell align="right">
                    {t('module.blindBox.lotteryResultsColVerification')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedIssue?.prizeTiers || [])
                  .slice()
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .flatMap((tier) => {
                    const slots = tier.winnerSlots || [];
                    if (slots.length) {
                      return slots
                        .slice()
                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                        .map((s) => ({
                          tierName: tier.tierName || '—',
                          position: s.position,
                          number: s.number,
                          txHash: s.txHash || '',
                        }));
                    }
                    // fallback: show winningNumbers as a single row if slots missing
                    const nums = (tier.winningNumbers || []).join(', ');
                    return nums
                      ? [
                          {
                            tierName: tier.tierName || '—',
                            position: 1,
                            number: nums,
                            txHash: '',
                          },
                        ]
                      : [];
                  })
                  .map((row, idx) => (
                    <TableRow key={`${row.tierName}-${row.position}-${idx}`}>
                      <TableCell>{row.tierName}</TableCell>
                      <TableCell>{row.position ?? '—'}</TableCell>
                      <TableCell>{row.number || '—'}</TableCell>
                      <TableCell>{truncateHash(row.txHash)}</TableCell>
                      <TableCell align="right">
                        {row.txHash ? (
                          <Link
                            href={`https://app.hyperliquid.xyz/explorer/tx/${row.txHash}`}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                          >
                            {t('module.blindBox.lotteryResultsView')}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                {!selectedIssue?.prizeTiers?.some(
                  (t2) => (t2.winnerSlots || []).length || (t2.winningNumbers || []).length
                ) && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        {t('module.blindBox.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/*<TextField*/}
          {/*  label={t('module.blindBox.drawTxHash')}*/}
          {/*  value={drawTxHash || '—'}*/}
          {/*  disabled*/}
          {/*  fullWidth*/}
          {/*  helperText={t('module.blindBox.drawTxHashHint')}*/}
          {/*/>*/}
        </Stack>
      </Box>

      <Dialog open={!!seasonEdit} onClose={() => setSeasonEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('module.blindBox.editSeason')}</DialogTitle>
        <DialogContent>
          {seasonEdit && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="id" value={seasonEdit.id} disabled fullWidth />
              <TextField
                label="seasonNumber"
                value={seasonEdit.seasonNumber}
                onChange={(e) => setSeasonEdit({ ...seasonEdit, seasonNumber: e.target.value })}
                disabled={loading}
                fullWidth
              />
              <TextField
                label={t('module.blindBox.colName')}
                value={seasonEdit.name}
                onChange={(e) => setSeasonEdit({ ...seasonEdit, name: e.target.value })}
                disabled={loading}
                fullWidth
              />
              <TextField
                label={t('module.blindBox.description')}
                value={seasonEdit.description || ''}
                onChange={(e) => setSeasonEdit({ ...seasonEdit, description: e.target.value })}
                disabled={loading}
                fullWidth
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="startTime"
                  value={seasonEdit.startTime ? dayjs(seasonEdit.startTime) : null}
                  onChange={handleSeasonEditStartChange}
                  disabled={loading}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="endTime"
                  value={seasonEdit.endTime ? dayjs(seasonEdit.endTime) : null}
                  onChange={handleSeasonEditEndChange}
                  disabled={loading}
                />
              </LocalizationProvider>
              <TextField
                label="status"
                value={seasonEdit.status}
                onChange={(e) => setSeasonEdit({ ...seasonEdit, status: e.target.value })}
                disabled={loading}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setSeasonEdit(null)}>
            {t('actions.cancel')}
          </Button>
          <PermissionGuard anyOf={SEASON_UPDATE_PERMS} fallback={null} loadingFallback={null}>
            <Button variant="contained" disabled={loading} onClick={saveSeasonEdit}>
              {t('actions.save')}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>

      <Dialog
        open={issueDlg}
        onClose={() => {
          if (creatingIssue) return;
          setIssueDlg(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('module.blindBox.createIssue')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="issueNumber"
              value={iform.issueNumber}
              onChange={(e) => setIform({ ...iform, issueNumber: e.target.value })}
              disabled={loading || creatingIssue}
              fullWidth
            />
            <TextField
              label={t('module.blindBox.colName')}
              value={iform.name}
              onChange={(e) => setIform({ ...iform, name: e.target.value })}
              disabled={loading || creatingIssue}
              fullWidth
            />
            <LotteryIssueNameI18nAccordion>
              <Grid container spacing={1.5}>
                {BB_LOTTERY_ISSUE_NAME_I18N_KEYS.map((loc) => (
                  <Grid item xs={12} sm={6} key={loc}>
                    <TextField
                      size="small"
                      label={t(`module.blindBox.lotteryIssueNameI18n_${loc}`)}
                      value={iform.nameI18n[loc] ?? ''}
                      onChange={(e) =>
                        setIform({
                          ...iform,
                          nameI18n: { ...iform.nameI18n, [loc]: e.target.value },
                        })
                      }
                      disabled={loading || creatingIssue}
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </LotteryIssueNameI18nAccordion>
            <TextField
              label={t('module.blindBox.ticketDigitCount')}
              type="number"
              inputProps={{ min: 1, max: 10, step: 1 }}
              value={iform.ticketDigitCount}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                setIform({
                  ...iform,
                  ticketDigitCount: Math.min(10, Math.max(1, n)),
                });
              }}
              disabled={loading || creatingIssue}
              fullWidth
              helperText={t('module.blindBox.ticketDigitCountHint')}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label={t('module.blindBox.lotteryIssueStartTime')}
                value={iform.startTime ? dayjs(iform.startTime) : null}
                onChange={handleIssueCreateStartChange}
                disabled={loading || creatingIssue}
                slotProps={{
                  textField: {
                    helperText: t('module.blindBox.lotteryIssueStartTimeOptionalHint'),
                  },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="endTime"
                value={iform.endTime ? dayjs(iform.endTime) : null}
                onChange={handleIssueCreateEndChange}
                disabled={loading || creatingIssue}
              />
            </LocalizationProvider>

            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('module.blindBox.prizePoolStructure')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                disabled={loading || creatingIssue}
                onClick={() => {
                  const nextOrder =
                    (iform.prizeTiers?.reduce((m, x) => Math.max(m, x.displayOrder || 0), 0) || 0) +
                    1;
                  setIform({
                    ...iform,
                    prizeTiers: [
                      ...(iform.prizeTiers || []),
                      { tierName: '', winnersCount: 1, rewardAmount: '1', displayOrder: nextOrder },
                    ],
                  });
                }}
              >
                {t('module.blindBox.addPrizeTier')}
              </Button>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.blindBox.colTier')}</TableCell>
                    <TableCell>{t('module.blindBox.colTierName')}</TableCell>
                    <TableCell>{t('module.blindBox.colRewardAmount')}</TableCell>
                    <TableCell>{t('module.blindBox.colWinnersCount')}</TableCell>
                    <TableCell align="right">{t('module.blindBox.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(iform.prizeTiers || []).map((tier, idx) => (
                    <TableRow key={tier.displayOrder ?? idx}>
                      <TableCell sx={{ width: 80 }}>{tier.displayOrder}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={tier.tierName}
                          disabled={loading || creatingIssue}
                          onChange={(e) => {
                            const next = [...(iform.prizeTiers || [])];
                            next[idx] = { ...tier, tierName: e.target.value };
                            setIform({ ...iform, prizeTiers: next });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={tier.rewardAmount}
                          disabled={loading || creatingIssue}
                          onChange={(e) => {
                            const next = [...(iform.prizeTiers || [])];
                            next[idx] = { ...tier, rewardAmount: e.target.value };
                            setIform({ ...iform, prizeTiers: next });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={tier.winnersCount}
                          disabled={loading || creatingIssue}
                          onChange={(e) => {
                            const next = [...(iform.prizeTiers || [])];
                            next[idx] = {
                              ...tier,
                              winnersCount: parseInt(e.target.value, 10) || 0,
                            };
                            setIform({ ...iform, prizeTiers: next });
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          disabled={loading || creatingIssue}
                          onClick={() => {
                            const next = (iform.prizeTiers || []).filter((_, i) => i !== idx);
                            setIform({ ...iform, prizeTiers: next });
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!iform.prizeTiers?.length && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t('module.blindBox.empty')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading || creatingIssue} onClick={() => setIssueDlg(false)}>
            {t('actions.cancel')}
          </Button>
          <PermissionButton resource="bb-holiday-lottery" action="create">
            <Button
              variant="contained"
              disabled={loading || creatingIssue}
              onClick={createIssue}
              startIcon={creatingIssue ? <CircularProgress color="inherit" size={18} /> : undefined}
            >
              {creatingIssue ? t('module.blindBox.savingIssue') : t('actions.save')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
